import { useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";

export default function BeforeAfter() {
  const ref = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(50);

  const update = useCallback((clientX) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(97, Math.max(3, p)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    update(e.clientX);
  };
  const onPointerMove = (e) => {
    if (dragging.current) update(e.clientX);
  };
  const stop = () => {
    dragging.current = false;
  };

  return (
    <section className="py-16 sm:py-24 px-4 sm:px-8 lg:px-16 bg-[#F1EFEA] border-t border-[#E5E2DC]" data-testid="before-after-section">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto"
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#244A32]">Vorher / Nachher</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-4">
            Aus Erde wird ein Garten
          </h2>
          <p className="mt-3 text-sm sm:text-base text-[#575953]">
            Ziehen Sie den Regler: Von der Planierung bis zum frisch verlegten Rollrasen – ein Projekt vom Neubau in Minden.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          ref={ref}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={stop}
          onPointerLeave={stop}
          className="relative mt-10 h-72 sm:h-[26rem] rounded-2xl overflow-hidden select-none cursor-ew-resize touch-none shadow-xl shadow-[#244A32]/10"
          data-testid="before-after-slider"
        >
          <img
            src="/gallery/planierung.webp"
            alt="Vorher: Planierung und Bodenverdichtung"
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            draggable={false}
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ clipPath: `inset(0 0 0 ${pos}%)` }}
          >
            <img
              src="/gallery/rollrasen.webp"
              alt="Nachher: Frisch verlegter Rollrasen"
              className="absolute inset-0 w-full h-full object-cover"
              draggable={false}
            />
          </div>
          <span className="absolute top-4 left-4 bg-[#141614]/70 backdrop-blur text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full pointer-events-none" data-testid="before-label">
            Vorher
          </span>
          <span className="absolute top-4 right-4 bg-[#244A32]/85 backdrop-blur text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full pointer-events-none" data-testid="after-label">
            Nachher
          </span>
          <div
            className="absolute top-0 bottom-0 w-1 bg-white shadow-lg pointer-events-none"
            style={{ left: `calc(${pos}% - 2px)` }}
          />
          <div
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-white shadow-xl flex items-center justify-center pointer-events-none"
            style={{ left: `${pos}%` }}
            data-testid="before-after-handle"
          >
            <MoveHorizontal className="w-5 h-5 text-[#244A32]" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
